import { Controller } from "@hotwired/stimulus"

// The ⋯ on a pin card in the arrange editor. Owns only the panel's visibility
// and the hand-off to the edit sheet: Remove is pin_remove_controller on the
// same element, and the sheet itself is edit_pin_controller on the editor root.
//
// The whole menu lives INSIDE the card, which is a link or a button, so every
// handler stops the event — otherwise opening the menu would follow the pin.
export default class extends Controller {
    static targets = ["trigger", "panel"]

    connect() {
        this._onDocumentClick = (event) => {
            if (!this.element.contains(event.target)) this.close()
        }
    }

    // Removing a pin takes its menu with it, so the folder has to get its clip
    // back here rather than in close() alone.
    disconnect() {
        document.removeEventListener("click", this._onDocumentClick)
        this._menuHost()?.classList.remove("is-menu-open")
    }

    toggle(event) {
        event?.preventDefault()
        event?.stopPropagation()
        if (this._open) this.close()
        else this.open()
    }

    open() {
        // One menu at a time: a second card's ⋯ closes the first, so a stray
        // panel never floats over the grid.
        document.querySelectorAll(".home-pin-card-menu-panel:not(.hidden)").forEach((panel) => panel.classList.add("hidden"))
        document.querySelectorAll(".home-pin-folder-panel-body.is-menu-open").forEach((body) => body.classList.remove("is-menu-open"))
        this.panelTarget.classList.remove("hidden")
        // A card inside a folder sits in a box that clips its overflow so the
        // fold-out can animate, and the panel hangs below the card — without
        // this the menu opens into nothing. Safe because the folder is already
        // open by the time its child's ⋯ is reachable.
        this._menuHost()?.classList.add("is-menu-open")
        this._flipIfClipped()
        this._open = true
        // Deferred, or the click that opened the menu closes it again.
        setTimeout(() => document.addEventListener("click", this._onDocumentClick), 0)
    }

    // Hanging below its row, the panel runs out of the pin dialog (which
    // scrolls, so clips) on the last rows: open it upward there instead.
    _flipIfClipped() {
        const panel = this.panelTarget
        panel.classList.remove("is-up")
        const box = this.element.closest("dialog, .home-pins-dialog")
        if (!box) return
        if (panel.getBoundingClientRect().bottom > box.getBoundingClientRect().bottom - 4) panel.classList.add("is-up")
    }

    close() {
        if (!this.hasPanelTarget) return
        this.panelTarget.classList.add("hidden")
        this._menuHost()?.classList.remove("is-menu-open")
        this._open = false
        document.removeEventListener("click", this._onDocumentClick)
    }

    // The clipping wrapper of the folder this card lives in, if any.
    _menuHost() {
        return this.element.closest(".home-pin-folder-panel-body")
    }

    // Hands the sheet everything it needs to render, so opening it costs no
    // request. The event bubbles to the editor root, where edit_pin listens.
    edit(event) {
        event?.preventDefault()
        event?.stopPropagation()
        const { url, linkUrl, title, favicon, emoji } = event.params
        this.close()
        this.dispatch("edit", { detail: { url, linkUrl, title, favicon, emoji }, prefix: "pin-menu" })
    }
}
